import { useRef, useState } from "react";
import { SectionTitle } from "../_shared/section-title";
import { PageShell, Stack } from "../_shared/page-shell";
import { Card } from "../_shared/card";
import { CameraSheet } from "../_shared/camera-sheet";
import { IconCamera, IconClose, IconPlus } from "../_shared/icons";
import { children, emergencyKinds, type Child } from "../_data/mock";

const childRing = {
  orange:
    "ring-[var(--child-1-primary)] bg-[var(--child-1-light)] text-[var(--child-1-primary)]",
  blue: "ring-[var(--child-2-primary)] bg-[var(--child-2-light)] text-[var(--child-2-primary)]",
} as const;

const MAX_PHOTOS = 3;

export function EmergencyPage() {
  const [childId, setChildId] = useState<Child["id"]>(
    children[0]?.id ?? "jeanne",
  );
  const [kindId, setKindId] = useState<string>(emergencyKinds[0]?.id ?? "");
  const [note, setNote] = useState("");
  const [photos, setPhotos] = useState<string[]>([]);
  const [cameraOpen, setCameraOpen] = useState(false);
  const galleryInputRef = useRef<HTMLInputElement | null>(null);

  const addPhoto = (dataUrl: string) => {
    setPhotos((prev) =>
      prev.length >= MAX_PHOTOS ? prev : [...prev, dataUrl],
    );
    setCameraOpen(false);
  };

  const removePhoto = (idx: number) => {
    setPhotos((prev) => prev.filter((_, i) => i !== idx));
  };

  const onGallery = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    const slots = MAX_PHOTOS - photos.length;
    files.slice(0, slots).forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        const url = reader.result;
        if (typeof url === "string") {
          setPhotos((prev) =>
            prev.length >= MAX_PHOTOS ? prev : [...prev, url],
          );
        }
      };
      reader.readAsDataURL(file);
    });
    e.target.value = "";
  };

  const canAddMore = photos.length < MAX_PHOTOS;

  return (
    <>
      <SectionTitle title="Photo d'urgence" />
      <PageShell>
        <Stack>
          <Card title="Pour qui ?">
            <ul className="flex flex-wrap gap-2 pt-1">
              {children.map((c) => {
                const selected = childId === c.id;
                return (
                  <li key={c.id}>
                    <button
                      type="button"
                      onClick={() => setChildId(c.id)}
                      className={`inline-flex h-10 items-center rounded-full px-4 text-[14px] font-semibold ring-1 transition ${selected ? childRing[c.color] : "bg-[var(--surface-1)] text-[var(--text-2)] ring-[var(--border-1)] hover:bg-[var(--surface-2)]"}`}
                    >
                      {c.firstName}
                    </button>
                  </li>
                );
              })}
            </ul>
          </Card>

          <Card title="Quel est le problème ?">
            <ul className="space-y-2 pt-1">
              {emergencyKinds.map((k) => {
                const selected = kindId === k.id;
                return (
                  <li key={k.id}>
                    <button
                      type="button"
                      onClick={() => setKindId(k.id)}
                      className={`flex w-full items-start gap-3 rounded-2xl border p-3 text-left transition ${selected ? "border-red-500 bg-red-50/70" : "border-[var(--border-1)] bg-[var(--surface-1)] hover:bg-[var(--surface-2)]"}`}
                    >
                      <span
                        className={`mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full border-2 ${selected ? "border-red-500 bg-red-500" : "border-[var(--border-2)]"}`}
                      >
                        {selected ? (
                          <span className="size-1.5 rounded-full bg-[var(--surface-1)]" />
                        ) : null}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-[14.5px] font-semibold text-[var(--text-1)]">
                          {k.label}
                        </p>
                        <p className="mt-0.5 text-[12.5px] text-[var(--text-2)]">
                          {k.example}
                        </p>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </Card>

          <Card title="Photo de la zone">
            <div className="flex flex-wrap gap-3 pt-1">
              {photos.map((url, i) => (
                <div
                  key={i}
                  className="relative h-28 w-28 overflow-hidden rounded-2xl bg-[var(--surface-2)] ring-1 ring-[var(--border-1)]"
                >
                  <img
                    src={url}
                    alt={`Photo ${i + 1}`}
                    className="size-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => removePhoto(i)}
                    aria-label={`Supprimer la photo ${i + 1}`}
                    className="absolute right-1.5 top-1.5 inline-flex size-7 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur transition active:scale-90"
                  >
                    <IconClose width={14} height={14} strokeWidth={2.5} />
                  </button>
                </div>
              ))}

              {canAddMore ? (
                <>
                  <button
                    type="button"
                    onClick={() => setCameraOpen(true)}
                    className="flex h-28 w-28 flex-col items-center justify-center gap-1.5 rounded-2xl border-2 border-dashed border-[var(--border-2)] bg-[var(--surface-2)] text-[var(--text-2)] transition hover:border-[var(--sf-primary)] hover:bg-[var(--sf-primary-100)] hover:text-[var(--sf-primary)]"
                  >
                    <IconCamera width={24} height={24} />
                    <span className="text-[11px] font-semibold">Prendre</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => galleryInputRef.current?.click()}
                    className="flex h-28 w-28 flex-col items-center justify-center gap-1.5 rounded-2xl border-2 border-dashed border-[var(--border-2)] bg-[var(--surface-2)] text-[var(--text-2)] transition hover:border-[var(--sf-primary)] hover:bg-[var(--sf-primary-100)] hover:text-[var(--sf-primary)]"
                  >
                    <IconPlus width={24} height={24} />
                    <span className="text-[11px] font-semibold">Galerie</span>
                  </button>
                  <input
                    ref={galleryInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={onGallery}
                    className="hidden"
                  />
                </>
              ) : null}
            </div>
            <p className="mt-3 text-[12px] text-[var(--text-2)]">
              {photos.length} / {MAX_PHOTOS} — 1 à 3 photos nettes, bien
              éclairées, montrant la zone gênante.
            </p>
          </Card>

          <CameraSheet
            open={cameraOpen}
            onClose={() => setCameraOpen(false)}
            onCapture={addPhoto}
          />

          <Card title="Quelques mots">
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={4}
              placeholder="Depuis quand ? Y a-t-il une douleur ? Avez-vous tenté quelque chose ?"
              className="block w-full resize-none rounded-2xl border border-[var(--border-1)] bg-[var(--surface-1)] p-3 text-[14px] outline-none transition focus:border-[var(--sf-primary)] focus:ring-2 focus:ring-[var(--sf-primary-100)]"
            />
          </Card>

          <button
            type="button"
            className="inline-flex h-12 w-full items-center justify-center rounded-full bg-[var(--sf-primary)] px-6 text-[15px] font-semibold text-white shadow-sm transition active:scale-[0.99] sm:mx-auto sm:max-w-sm"
          >
            Envoyer au cabinet
          </button>
          <p className="-mt-1 text-center text-[11.5px] text-[var(--text-2)]">
            Réponse en moyenne sous 4 heures (jours ouvrés)
          </p>
        </Stack>
      </PageShell>
    </>
  );
}
